<script lang="ts">
	/**
	 * "All devices" header kebab. A single ⋮ trigger opens a shared dropdown
	 * with two sections composed from dedicated sub-components:
	 *  - Bulk actions (`AllDevicesDropdownBulkActions`)
	 *  - Change view (`AllDevicesDropdownViewsSelection`)
	 *
	 * When the user starts a bulk operation the trigger is replaced by an
	 * inline "select all / <action> / cancel" row (same UX as the legacy
	 * `BatchCommandButtons`). Shared modals (AI commands, mobile options,
	 * shared-home managed) live here so the sub-components stay focused.
	 *
	 * This component is wired through `$listHeader.extraActions` from
	 * `DevicesToolbar.svelte` and rendered in `MainLayoutNew`'s sub-tabs
	 * action slot.
	 */
	import { createEventDispatcher } from 'svelte';
	import { format } from 'svelte-i18n';
	import {
		Button,
		Checkbox,
		DropdownMenu,
		IconButton,
		type IconButtonPropsTypes,
	} from '@Shelly/shelly-design-system';
	import { classMap } from '@Shelly/shelly-design-system/helpers/component-utils';
	import { SystemScreenBreakpoints } from '@Shelly/shelly-design-system/helpers/enums';
	import { resetDrawer } from 'src/helpers/drawer_helpers';
	import { resetFiltersAllDevicesList } from 'src/stores/ui/filters';
	import { devices, home_data, user_data } from 'src/stores';
	import {
		update,
		edit,
		initialEdit,
		bulkActionEdit,
		resetEditUpdateStore,
		type UpdateModeSubTypes,
		type UpdateModeTypes,
	} from 'src/stores/ui/edit_lists';
	import { homes } from 'src/stores/account/home';
	import { onSettingsResponse } from 'src/helpers/device_settings_helpers';
	import type { DeviceListLayoutType } from 'src/helpers/new-layout-helpers';
	import { isActiveHomeShared } from 'src/helpers/houses/homes_helpers';
	import { getButtonText } from 'src/helpers/device_data_helpers';
	import { buildDropdownMenus } from 'src/components/Scenes/helpers/menuBuilders';
	import ChooseAIModalModal from 'src/components/ChooseAIModalModal.svelte';
	import BatchCommandModal from 'src/components/BatchCommandModals/BatchCommandModal.svelte';
	import SharedHomeOwnerManagedModal from 'src/components/SharedHome/SharedHomeOwnerManagedModal.svelte';
	import TabsActionsButton from '../../../TabsActionsButton.svelte';
	import AllDevicesDropdownBulkActions from './AllDevicesDropdownBulkActions.svelte';
	import AllDevicesDropdownViewsSelection from './AllDevicesDropdownViewsSelection.svelte';
	import ListHeaderSearch from 'src/components/Lists/ListHeaderSearch.svelte';
	import { listHeader } from 'src/stores/list_header';
	import { saveDeviceListSettings } from 'src/helpers/home_helpers';
	import { showNewLayout } from 'src/stores/toDelete/new_layout';
	import { deviceTableViewAvailable } from 'src/stores/features/dev/device-table-view';

	const dispatchSelectAll = createEventDispatcher<{ 'select-all': {} }>();
	const dispatchSubmit = createEventDispatcher<{ 'submit-click': {} }>();

	export let disableBatchCommandButton = false;
	export let areFiltersDisabled = false;
	/**
	 * Trigger IconButton variant. Default matches the dashboards kebab
	 * (`muted`, transparent) so this kebab reads consistently across every
	 * list living in the sub-tabs action slot.
	 */
	export let triggerVariant: IconButtonPropsTypes['variant'] = 'muted';

	// Adapter surface for `extraActions` (which passes callbacks, not events).
	// Both events and callbacks are supported so this component works either
	// way (event forwarding for direct usage, callbacks for adapter usage).
	export let onSelectAll: (() => void) | undefined;
	export let onSubmitClick: (() => void) | undefined;

	let showDropdown = false;
	let showModalMenu = false;
	let showAIModelCommandsModal = false;
	let showSharedManagedModal = false;

	let windowClientWidth: number;
	$: isMobile = windowClientWidth < SystemScreenBreakpoints.MOBILE_MAX;
	$: isTablet = windowClientWidth <= SystemScreenBreakpoints.TABLET_MAX;
	$: kebabSearch = $listHeader?.search ?? null;
	$: showKebabSearch = !!kebabSearch && (isMobile || isTablet);
	$: kebabSearchForModal =
		kebabSearch && showKebabSearch
			? {
					...kebabSearch,
					filter: kebabSearch.filter
						? {
								...kebabSearch.filter,
								onClick: () => {
									showModalMenu = false;
									kebabSearch.filter?.onClick();
								},
							}
						: undefined,
				}
			: null;
	$: selectedDevicesLength = Object.keys($update.selectedDevices).length;
	$: buttonText = customButtonText || getButtonText($update.type, $format);
	$: isEmptyDeviceList = Object.values($devices).length === 0;
	$: submitButtonExtras =
		selectedDevicesLength > 0
			? {
					notification: {
						type: 'danger' as const,
						title: selectedDevicesLength,
					},
				}
			: {};

	let customButtonText = '';

	let dropdownMenuInitialEditCommands: any[] = [];
	let dropdownMenuBulkCommands: any[] = [];

	// Built here (not inside BulkActions) so mobile `BatchCommandModal` always
	// receives data — `DropdownMenu` only mounts its slot when `show === true`.
	$: {
		const { initialEditCommands, bulkCommands } = buildDropdownMenus({
			format: $format,
			userData: $user_data,
			setShowDropdownMenu: (v) => {
				if (!v) closeParentDropdown();
			},
			setShowAIModelCommandsModal: (v) => {
				if (v) showAIModelCommandsModal = true;
			},
			onChangeClick,
			updateStore,
			newLayout: $showNewLayout,
		});
		dropdownMenuInitialEditCommands = initialEditCommands;
		dropdownMenuBulkCommands = bulkCommands;
	}

	$: dropdownMenuMenuData = $initialEdit ? dropdownMenuInitialEditCommands : dropdownMenuBulkCommands;

	// View-selection items surfaced in the mobile modal so it mirrors what the
	// desktop dropdown shows. Kept in sync with the visible view via `home_data`.
	$: currentView = ($home_data?.['device_list']?.type as DeviceListLayoutType) || 'default';
	const onChangeViewFromModal = async (type: DeviceListLayoutType) => {
		if (currentView === type) return;
		const res = await saveDeviceListSettings({ type });
		onSettingsResponse(res);
	};
	$: viewModalItems = [
		{
			title: $format('label_list_view', { default: 'List view' }),
			icon: 'fas fa-list',
			disabled: currentView === 'default',
			onClick: () => onChangeViewFromModal('default'),
		},
		{
			title: $format('label_grid_view', { default: 'Grid view' }),
			icon: 'fas fa-grid-2',
			disabled: currentView === 'compact',
			onClick: () => onChangeViewFromModal('compact'),
		},
		...($deviceTableViewAvailable
			? [
					{
						title: $format('label_table_view', { default: 'Table view' }),
						icon: 'fas fa-table',
						disabled: currentView === 'table',
						badge: 'dev enabled',
						onClick: () => onChangeViewFromModal('table'),
					},
				]
			: []),
	];

	const AIModelCommands = [
		{
			name: 'text_enable_ai_model',
			id: 'enable_ai_model' as UpdateModeSubTypes,
			icon: 'fak fa-solid-sparkles icon-ai-color',
		},
		{
			name: 'text_disable_ai_model',
			id: 'disable_ai_model' as UpdateModeSubTypes,
			icon: 'fak fa-solid-sparkles',
		},
		{
			name: 'manage_ai_notifications',
			id: 'manage_ai_notifications' as UpdateModeSubTypes,
			icon: 'fa-light fa-bell',
		},
	];

	const onToggleDropdown = () => {
		if (!isMobile && !isTablet) {
			showDropdown = !showDropdown;
		} else {
			showModalMenu = !showModalMenu;
		}
	};

	const closeParentDropdown = () => {
		showDropdown = false;
		showModalMenu = false;
	};

	// Bridge callback style (extraActions) → event style (parent usage).
	const emitSelectAll = (event: any) => {
		if (onSelectAll) onSelectAll();
		else dispatchSelectAll('select-all', event);
	};
	const emitSubmit = () => {
		if (onSubmitClick) onSubmitClick();
		else dispatchSubmit('submit-click');
	};

	// TS cast is illegal inside Svelte template expressions — pull the input's
	// checked state out here so the click handler stays declarative.
	const handleSelectAllClick = (event: Event) => {
		const target = event.target as HTMLInputElement;
		update.update((s) => ({ ...s, selectAll: target.checked }));
		edit.set(true);
		emitSelectAll(event);
	};

	// Mirror `BatchCommandButtons` AI subType helper for the modal.
	const getAICommandText = (subType: UpdateModeSubTypes) =>
		AIModelCommands.find((c) => c.id === subType)
			? $format(AIModelCommands.find((c) => c.id === subType)!.name)
			: '';

	const updateStore = (
		type: UpdateModeTypes,
		subType: UpdateModeSubTypes = '' as UpdateModeSubTypes,
	) => {
		bulkActionEdit.set(true);
		areFiltersDisabled = true;
		update.update((s) => ({
			...s,
			value: true,
			type,
			subType,
		}));
		edit.set(true);
	};

	const onChangeClick = (
		type: UpdateModeTypes,
		subType: UpdateModeSubTypes = '' as UpdateModeSubTypes,
	) => {
		closeParentDropdown();
		updateStore(type, subType);
		resetFiltersAllDevicesList();

		if (type === 'ai_device_failure' && subType) {
			customButtonText = getAICommandText(subType);
		} else {
			customButtonText = '';
		}
	};

	// Called when the mobile modal / AI modal picks a bulk command.
	const onAiCommandClick = (type: UpdateModeTypes, subType: UpdateModeSubTypes) => {
		onChangeClick(type, subType);
	};
</script>

<svelte:window bind:innerWidth={windowClientWidth} />

{#if isActiveHomeShared($homes.list)}
	<TabsActionsButton variant={triggerVariant} on:click={() => (showSharedManagedModal = true)} />
	<SharedHomeOwnerManagedModal bind:showModal={showSharedManagedModal} context="generic" />
{:else if !isActiveHomeShared($homes.list)}
	{#if !$edit || !$update.value}
		<DropdownMenu bind:show={showDropdown} class="align-self-center">
			<TabsActionsButton
				slot="trigger"
				variant={triggerVariant}
				isActive={showDropdown}
				disabled={disableBatchCommandButton}
				on:click={onToggleDropdown}
			/>

			<AllDevicesDropdownBulkActions
				{dropdownMenuMenuData}
				{onChangeClick}
				on:item-click={closeParentDropdown}
			/>

			<div class="all-devices-dropdown-divider"></div>

			<AllDevicesDropdownViewsSelection
				sectionTitle={$format('label_change_view', { default: 'Change view' })}
				on:item-click={closeParentDropdown}
			/>
		</DropdownMenu>
	{:else}
		<div
			class={classMap({
				'd-flex align-items-center': true,
				'flex-wrap': isMobile,
			})}
		>
			<div class="d-flex width-full">
				<Checkbox
					size="m"
					id="checkbox-select-all"
					class="flex-shrink-0 mr-s text-capitalize"
					checked={$update.selectAll}
					disabled={$update.subType === 'enable_ai_model'}
					on:click={handleSelectAllClick}
				>
					{$format('lebel_select_all', { default: 'select all' })}
				</Checkbox>
				<Button
					variant="primary"
					size="m"
					class="width-md-auto width-sm-full mr-sm-s mr-md-s text-capitalize"
					on:click={emitSubmit}
					{...submitButtonExtras}
					disabled={selectedDevicesLength === 0}
				>
					{buttonText}
				</Button>
				<IconButton
					aria-label={$format('cancel', { default: 'Cancel' })}
					variant="secondary"
					icon="fas fa-times"
					on:click={() => {
						areFiltersDisabled = false;
						resetEditUpdateStore();
						resetFiltersAllDevicesList();
						customButtonText = '';
					}}
				/>
			</div>
		</div>
	{/if}

	<ChooseAIModalModal
		aiModelCommands={AIModelCommands}
		bind:showAIModelCommandsModal
		onChangeClick={onAiCommandClick}
	/>

	<BatchCommandModal
		bind:showModalMenu
		{dropdownMenuMenuData}
		{isEmptyDeviceList}
		{AIModelCommands}
		viewSectionTitle={$format('label_change_view', { default: 'Change view' })}
		viewItems={viewModalItems}
		on:close={() => (showModalMenu = false)}
		on:menu-item-click={(e) => {
			resetDrawer();
			e.detail.callback?.();
			initialEdit.set(false);
		}}
		on:ai-command-click={(e) => onAiCommandClick(e.detail.type, e.detail.subType)}
	>
		<div slot="header">
			{#if showKebabSearch && kebabSearchForModal}
				<ListHeaderSearch search={kebabSearchForModal} className="mb-m" />
			{/if}
		</div>
	</BatchCommandModal>
{/if}

<style>
	.all-devices-dropdown-divider {
		border-top: 1px solid var(--color-3);
	}
</style>
